import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BellRing, Bike, ChevronDown, Clock3, Gift, Heart, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Toaster, toast } from "@/components/ui/sonner";

const TRAVEL_ROUTE_MAP_URL = "https://maps.app.goo.gl/1JoZYTfjCANXwR2e7?g_st=ac";
const VENAFRO_ADDRESS_TOKEN = "__VENAFRO_ADDRESS__";
const VENAFRO_ADDRESS_LABEL = "Strada Comunale Romana, 37 - Venafro";
const VENAFRO_ADDRESS_MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Strada+Comunale+Romana+37+Venafro";

const locationBlocks = [
  {
    key: "cerimonia",
    title: "Cerimonia",
    name: "Basilica dell'Incoronata Madre del Buon Consiglio",
    address: "Via Capodimonte, 13 - Napoli",
    phone: "081 7413567",
    website: "https://basilicacapodimonte.it/",
    mapQuery: "Basilica dell'Incoronata Madre del Buon Consiglio",
    mapUrl: "https://maps.app.goo.gl/6QtqGcu2ntHTS8uE7",
  },
  {
    key: "ricevimento",
    title: "Ricevimento",
    name: "Al Chiar di Luna",
    address: "Via Amedeo, 89 - Monte di Procida (Napoli)",
    phone: "081 8682424",
    website: "https://www.alchiardiluna.it/",
    mapQuery: "Al Chiar di Luna Monte di Procida Napoli",
    mapUrl: "https://maps.app.goo.gl/W1Kb1NS17frtH13o8",
  },
];

const timelineItems = [
  "Ore 10.30 – Rinfresco tradizionale presso la casa della sposa",
  "Ore 11.30 – Preparativi dello sposo insieme ai suoi testimoni",
  "Ore 13.00 – Partenza dell'autobus privato da Ceppagna (Piazza Padre Pio) in direzione Napoli",
  "Ore 15.00 – Cerimonia nuziale",
  "Ore 17.30 – Inizio del ricevimento",
];

const eventNewsItems = [
  {
    title: "🎶La nostra serenata: una tradizione che cambia voce",
    publishedOn: "2 settembre 2026",
    message:
      "**Sabato 29 agosto** è andata in scena una delle tradizioni più sentite a Ceppagna: la **serenata**.\n\nLe tradizioni vanno rispettate, certo. Ma forse il modo più bello per tenerle vive è anche quello di **reinterpretarle**, facendole un po’ proprie.\n\nCosì lo sposo, non sapendo cantare — e volendo evitare inutili sofferenze agli invitati — al posto della classica canzone ho scelto di recitare una propria **poesia**.\n\nUn piccolo cambio di spartito, senza cambiare il senso della tradizione: **dedicare qualcosa di personale alla persona che si ama**.\n\nE questa è stata la nostra **serenata**. ✨",
  },
  {
    title: "🎶 Una serata sotto il balcone",
    publishedOn: "23 agosto 2026",
    message:
      `**Sabato 29 agosto**, dalle **ore 20:00**, a **casa della sposa (${VENAFRO_ADDRESS_TOKEN})** andrà in scena una delle tradizioni più sentite a **Ceppagna**.\n\nLa sposa aspetta con entusiasmo. Lo sposo, con la consapevolezza che per una sera stare al centro della scena farà parte del programma, volente o nolente.\n\nSul resto manteniamo un po’ di riserbo: ci sarà musica, non mancheranno i brindisi e la tavola farà la sua parte, il tutto condito dall’immancabile ospitalità ceppagnola.\n\nPer sapere come andrà a finire, non resta che esserci.`,
  },
  {
    title: "✔️ Conferma la tua presenza al matrimonio",
    publishedOn: "30 luglio 2026",
    message:
      "Per aiutarci a organizzare al meglio ogni dettaglio della giornata, **ti chiediamo di confermare la tua partecipazione** compilando il **modulo** presente in questo invito digitale. Ti saremo grati se vorrai inviare la tua risposta **entro il 31 agosto**.",
  },
  {
    title: "🚌 Servizio autobus da Ceppagna",
    publishedOn: "25 luglio 2026",
    message:
      "Per gli ospiti che partiranno da **Ceppagna** è disponibile un **servizio autobus** sia all'andata che al ritorno, con partenza e rientro presso **Piazza Padre Pio**.\n\nPer consentirci di organizzare al meglio il servizio, vi chiediamo gentilmente di **confermare la vostra adesione il prima possibile contattando i genitori della sposa**.\n\n👨‍🦲 **Michele** – tel. 3296471991\n👩‍🦰 **Orietta** – tel. 3282709978",
  },
  {
    title: "🦁 Cronache di un weekend... da leoni.",
    publishedOn: "21 luglio 2026",
    message:
      "Prima del grande giorno c'era una tradizione da rispettare.\n\nLei ha scelto **Ischia**, lui la **Costa Smeralda**. Due mete diverse, un solo obiettivo: **celebrare con leggerezza la fine di un capitolo e l'inizio di quello più bello.**\n\nTra tramonti, brindisi, risate e qualche episodio che continuerà a vivere soltanto nei racconti dei presenti, **entrambi gli \"equipaggi\" hanno portato a termine la missione con successo.**\n\n**Nessun dettaglio compromettente verrà divulgato.** Possiamo soltanto confermare che gli sposi sono rientrati sani e salvi... **e che il matrimonio del 26 settembre è ancora regolarmente in programma.**",
  },
  {
    title: "🖋️ Troppo tardi per ripensarci: abbiamo firmato.",
    publishedOn: "21 giugno 2026",
    message:
      "Il **20 giugno**, alle 18:30, presso il **Municipio di Venafro**, abbiamo celebrato la **promessa di matrimonio** alla presenza del Sindaco.\n\nÈ stato un momento semplice e autentico, condiviso con le nostre famiglie: **un passaggio che segna ufficialmente l'inizio dell'ultimo tratto di strada verso il 26 settembre**. Tra sorrisi, emozioni e qualche inevitabile batticuore, una semplice firma è diventata il simbolo di un impegno che avevamo già scelto da tempo.\n\nPer rendere quel gesto ancora più speciale, la firma è stata apposta con una penna dal valore inestimabile.",
    quote:
      "Nella mano stringo la penna di papà.\nÈ spuntata dall'astuccio\nfra cose che non cercavo:\ncasuale, decisiva, non pianificata.\n«Quando faccio una promessa\nmantengo sempre la parola.»\nSogno.\nCredo.\nRealizzo.",
    closing: "Adesso manca soltanto il passo più bello: **vivere quel giorno insieme a tutti voi**.",
  },
];

const faqSections = [
  {
    key: "dress-code",
    title: "👔 Dress Code",
    items: [
      {
        question: "È previsto un dress code?",
        answer:
          "Abbiamo immaginato uno stile ispirato alla luce del tramonto sul mare: raffinato, ma naturale. Un’eleganza senza rigidità, fatta di equilibrio e leggerezza.",
      },
      {
        question: "Ci sono indicazioni particolari da seguire?",
        answer:
          "La cerimonia si svolgerà in basilica, mentre parte del ricevimento avrà luogo all’aperto. Per questo, consigliamo di scegliere un abbigliamento adeguato a entrambi i momenti, tenendo conto anche del comfort durante gli spostamenti.",
      },
      {
        question: "Qualche suggerimento pratico?",
        answer:
          "Per le signore, le location non presentano superfici disagevoli: potrete scegliere liberamente le calzature che preferite, in linea con il vostro stile e la vostra naturale sensibilità per l’eleganza.\n\nPer gli uomini, uno stile elegante, essenziale e curato sarà perfettamente in sintonia con l’atmosfera della giornata: giacca, linee pulite e calzature classiche, evitando capi casual come il denim.",
      },
      {
        question: "Ci sono colori da evitare o preferire?",
        answer:
          "Non ci sono indicazioni rigide: sentitevi liberi di scegliere ciò che vi rappresenta. Tonalità luminose e naturali, ispirate ai colori del tramonto sul mare, accompagneranno con eleganza l’atmosfera della giornata.",
      },
    ],
  },
  {
    key: "cerimonia",
    title: "💒 Cerimonia",
    items: [
      {
        question: "Quanto dura la cerimonia?",
        answer:
          "Indicativamente un’ora. Vi consigliamo di arrivare con qualche minuto di anticipo, così da vivere con serenità l’inizio della celebrazione.",
      },
      {
        question: "La cerimonia ha un particolare significato religioso?",
        answer:
          "La celebrazione si svolgerà secondo il rito cattolico. Per noi rappresenta un momento importante di raccoglimento e condivisione, che saremo felici di vivere insieme a voi.",
      },
      {
        question:
          "Ci sono indicazioni particolari per l’abbigliamento durante la cerimonia?",
        answer:
          "La cerimonia si svolgerà in basilica, in un contesto raccolto e solenne. Per eventuali abiti con scollature più pronunciate, può essere utile prevedere una stola, uno scialle o un accessorio leggero da indossare durante la celebrazione.",
      },
      {
        question: "È possibile fare fotografie durante la cerimonia?",
        answer:
          "Vi invitiamo a vivere questo momento con discrezione e partecipazione. Un fotografo professionista si occuperà di documentare la celebrazione; per questo vi chiediamo, se possibile, di limitare l’utilizzo di smartphone e dispositivi fotografici durante il rito.",
      },
      {
        question: "La basilica è accessibile a tutti?",
        answer:
          "Sì, la basilica dispone di percorsi che consentono l’accesso a tutti. Qualora aveste esigenze particolari, vi invitiamo a contattarci in anticipo, così da potervi fornire il supporto necessario.",
      },
    ],
  },
  {
    key: "logistica",
    title: "🚗 Logistica",
    items: [
      {
        question: "Dove posso parcheggiare?",
        answer:
          "Sono disponibili parcheggi gratuiti e custoditi in entrambe le location: presso la basilica, nell’area esterna adiacente, e presso il luogo del ricevimento. Non è necessario effettuare prenotazioni: i posti sono già riservati a tutti gli invitati.",
      },
      {
        question: "È previsto un supporto per chi parte da Venafro?",
        answer:
          "Sì, per gli ospiti in partenza da Venafro è disponibile un servizio autobus dedicato, che accompagnerà sia alla cerimonia sia al ricevimento. Per informazioni su orari, punti di ritrovo e disponibilità del servizio, è possibile fare riferimento alla sposa o alla sua famiglia.\n\nPer chi preferisse muoversi in autonomia, il percorso è diretto e facilmente gestibile in auto. Si consiglia di percorrere l’autostrada A1 in direzione Napoli, accedendo dal casello di San Vittore, così da ridurre al minimo il tratto su viabilità interna.",
      },
      {
        question: "È previsto uno spostamento tra le due location?",
        answer:
          "Sì, la cerimonia e il ricevimento si svolgeranno in luoghi distinti, ma il percorso è semplice e diretto. Dalla basilica si accede facilmente alla Tangenziale di Napoli, che conduce alla location del ricevimento.",
      },
      {
        question: "Sono disponibili indicazioni per il percorso?",
        answer:
          "Per chi arriva in auto, consigliamo di impostare il navigatore utilizzando le coordinate indicate nell’invito.\n\n**Per la cerimonia**\nÈ consigliabile percorrere la Tangenziale di Napoli con uscita a Capodimonte. Una volta usciti, dopo poche centinaia di metri, la basilica si presenterà sulla destra, ben visibile.\n\n**Per il ricevimento**\nDalla basilica si accede facilmente alla Tangenziale di Napoli in direzione Pozzuoli, con uscita ad Arco Felice. Una volta in zona, sarà sufficiente seguire il navigatore, che indicherà un percorso semplice e lineare.\n\nIn prossimità della location, nel comune di Monte di Procida, sono presenti chiare indicazioni lungo il percorso. L’ultimo tratto — circa cinque minuti di percorrenza — si sviluppa su strade più raccolte, ma ben organizzate: sono a senso unico e adeguatamente segnalate, così da rendere l’arrivo agevole e senza difficoltà.\n\nAnche nei tratti più stretti il passaggio è garantito, come dimostra il transito regolare di navette di servizio.",
      },
    ],
  },
  {
    key: "ricevimento",
    title: "🍾 Ricevimento",
    items: [
      {
        question: "A che ora inizia il ricevimento?",
        answer:
          "Il ricevimento avrà inizio dopo la cerimonia: gli orari sono indicati nella timeline dell’invito. I tempi sono stati pensati per permettere a tutti di raggiungere la location con calma.",
      },
      {
        question: "Come sarà organizzato il ricevimento?",
        answer:
          "La giornata si svilupperà in diversi momenti, alternando convivialità, musica e spazi da vivere con leggerezza. Abbiamo immaginato un’atmosfera dinamica ma rilassata, in cui ognuno possa sentirsi a proprio agio.",
      },
      {
        question: "È previsto un tavolo assegnato?",
        answer:
          "Sì, ogni ospite troverà la propria assegnazione all’arrivo presso la location del ricevimento.",
      },
      {
        question: "Sono previsti momenti all’aperto?",
        answer:
          "Sì, parte del ricevimento si svolgerà all’aperto: vi consigliamo di tenerne conto nella scelta dell’abbigliamento. A questo proposito, vi invitiamo, con un pizzico di complicità, a contribuire anche voi al bel tempo.\n\nIn caso di condizioni meteo avverse, la struttura dispone di eleganti spazi interni che permetteranno di vivere la giornata con la stessa serenità.",
      },
      {
        question: "La location è accessibile a tutti?",
        answer:
          "Sì, la struttura è stata scelta anche per garantire comfort e facilità di movimento a tutti gli ospiti, così che ognuno possa vivere la giornata in piena serenità.",
      },
      {
        question: "È possibile allontanarsi durante il ricevimento?",
        answer:
          "Certamente, la location consente libertà di movimento. Potrete vivere la giornata con i vostri tempi, senza vincoli particolari.",
      },
      {
        question: "Sono previsti intrattenimento e musica?",
        answer:
          "Sì, la giornata sarà accompagnata da musica, rigorosamente strumentale, e da momenti di intrattenimento pensati per arricchire l’atmosfera con discrezione e armonia.",
      },
      {
        question: "È possibile fare foto durante la giornata?",
        answer:
          "Certo, ma vi invitiamo — almeno per qualche momento — a concedere una pausa allo smartphone. Abbiamo scelto con cura un fotografo che saprà raccontare la giornata attraverso i suoi scatti, così da permettere a tutti di viverla davvero, senza distrazioni.\n\nDel resto, i ricordi più belli non hanno bisogno di uno schermo.",
      },
      {
        question: "Ci sono indicazioni particolari da tenere a mente?",
        answer:
          "Solo una: godersi la giornata. Senza fretta, senza programmi rigidi, lasciandosi semplicemente guidare dal momento. Al resto abbiamo pensato noi.",
      },
    ],
  },
  {
    key: "bambini",
    title: "👶 Bambini",
    items: [
      {
        question: "I bambini sono i benvenuti?",
        answer: "Sì, i bambini sono i benvenuti.",
      },
      {
        question: "Ci sono indicazioni particolari per la cerimonia?",
        answer:
          "La cerimonia si svolge in basilica, in un contesto raccolto: vi chiediamo quindi di accompagnare i più piccoli con particolare attenzione durante questo momento.",
      },
      {
        question: "La location del ricevimento è adatta ai bambini?",
        answer:
          "La location è sicura e adatta ad accogliere anche i più piccoli. Come in ogni contesto, vi chiediamo semplicemente di seguirli con cura durante la giornata.",
      },
      {
        question: "È prevista animazione per i bambini?",
        answer:
          "Sì, durante il ricevimento è previsto un servizio di animazione dedicato ai bambini dai 5 anni in su. I bambini rientranti in questa fascia saranno accolti in una sala dedicata e affidati agli animatori, che li accompagneranno durante le attività pensate per loro.\n\nPer i bambini sotto i 5 anni non è previsto un servizio dedicato, per cui sarà necessaria la presenza e l’attenzione dei genitori durante la giornata.",
      },
      {
        question: "Sono previsti menù dedicati ai bambini?",
        answer:
          "Sì, è possibile prevedere soluzioni dedicate ai più piccoli. Vi chiediamo di segnalarlo al momento della conferma della presenza.",
      },
    ],
  },
  {
    key: "intolleranze",
    title: "🍽️ Intolleranze, allergie ed esigenze alimentari",
    items: [
      {
        question: "È possibile segnalare allergie, intolleranze o esigenze alimentari?",
        answer:
          "Certamente. Vi chiediamo gentilmente di indicarci eventuali allergie, intolleranze o esigenze specifiche al momento della conferma della presenza. Questo ci permetterà di organizzare al meglio il servizio e di garantire a tutti un’esperienza serena.\n\nLa cucina della struttura opera nel pieno rispetto delle normative vigenti per la gestione di allergie e intolleranze lungo tutta la filiera di preparazione, con la massima attenzione alla sicurezza degli ospiti.",
      },
    ],
  },
  {
    key: "pernottamento",
    title: "🏨 Pernottamento",
    items: [
      {
        question: "Ci sono suggerimenti per il pernottamento?",
        answer:
          "La location del ricevimento dispone anche di camere per chi desidera fermarsi in loco. Per chi lo preferisse, saremo felici di offrire supporto nella ricerca di un alloggio nei pressi della struttura, così da individuare la soluzione più adatta alle proprie esigenze.\n\nNon esitate a contattarci: vi aiuteremo volentieri nell’organizzazione del soggiorno.",
      },
      {
        question: "È possibile lasciare l’auto in struttura durante la notte?",
        answer:
          "Sì, gli ospiti che pernotteranno in struttura potranno lasciare l’auto nel parcheggio della location.",
      },
    ],
  },
  {
    key: "rsvp",
    title: "✉️ RSVP",
    items: [
      {
        question: "Come posso confermare la mia presenza?",
        answer:
          "Potete confermare la vostra partecipazione attraverso il modulo presente in questo sito.",
      },
      {
        question: "Entro quando è richiesta la conferma?",
        answer:
          "Vi chiediamo gentilmente di confermare **entro il 31 agosto**, così da permetterci di organizzare al meglio ogni dettaglio.",
      },
      {
        question: "Posso modificare la mia risposta dopo aver confermato?",
        answer:
          "Certamente. Vi chiediamo soltanto di comunicarci eventuali variazioni il prima possibile, così da permetterci di aggiornare l’organizzazione dell’evento.",
      },
    ],
  },
];

const countdownLabels = [
  { key: "hours", label: "ORE" },
  { key: "minutes", label: "MINUTI" },
  { key: "seconds", label: "SECONDI" },
];

const ibanDetails = {
  iban: "IT62Z0306234210000003177709",
  bank: "Mediolanum",
  bic: "MEDBITMM",
  holders: "Angela Antonelli e Pierpaolo Torelli",
};

const spouseContacts = [
  {
    key: "sposo",
    title: "🤵‍♂️ Lo Sposo",
    name: "Pierpaolo Torelli",
    address: "Via N. A. Porpora, 7B - 80128 Napoli",
    phone: "351 8797180",
    telLink: "3518797180",
    email: "paolotorelli@me.com",
  },
  {
    key: "sposa",
    title: "👰‍♀️ La Sposa",
    name: "Angela Antonelli",
    address: "Strada Comunale Romana, 37 - 86079 Venafro (IS)",
    phone: "333 8712084",
    telLink: "3338712084",
    email: "ange.antonelli@icloud.com",
  },
];

const createWeddingDate = () => {
  return new Date(2026, 8, 26, 15, 0, 0);
};

const calculateCountdown = (targetDate) => {
  const distance = targetDate.getTime() - Date.now();

  if (distance <= 0) {
    return { hours: 0, minutes: 0, seconds: 0, expired: true };
  }

  const totalHours = Math.floor(distance / (1000 * 60 * 60));

  return {
    hours: totalHours,
    minutes: Math.floor((distance / (1000 * 60)) % 60),
    seconds: Math.floor((distance / 1000) % 60),
    expired: false,
  };
};

function App() {
  const weddingDate = useMemo(() => createWeddingDate(), []);
  const [countdown, setCountdown] = useState(() => calculateCountdown(weddingDate));
  const [envelopeOpen, setEnvelopeOpen] = useState(0);
  const [showIban, setShowIban] = useState(false);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCountdown(calculateCountdown(weddingDate));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [weddingDate]);

  useEffect(() => {
    const onScroll = () => {
      const scrollDepth = window.scrollY;
      const openDistance = window.innerWidth <= 640 ? 145 : 220;
      const nextProgress = Math.min(scrollDepth / openDistance, 1);
      setEnvelopeOpen(nextProgress);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const blocks = document.querySelectorAll('[data-reveal="true"]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    blocks.forEach((block) => observer.observe(block));

    return () => observer.disconnect();
  }, []);

  const handleIbanToggle = () => setShowIban((prev) => !prev);

  const handleCopyIban = async () => {
    const fallbackCopy = () => {
      const textArea = document.createElement("textarea");
      textArea.value = ibanDetails.iban;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textArea);
      return copied;
    };

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(ibanDetails.iban);
        toast.success("IBAN copiato");
        return;
      }

      const copiedFallback = fallbackCopy();
      if (copiedFallback) {
        toast.success("IBAN copiato");
      } else {
        toast.error("Copia non riuscita");
      }
    } catch (error) {
      const copiedFallback = fallbackCopy();
      if (copiedFallback) {
        toast.success("IBAN copiato");
      } else {
        toast.error("Copia non riuscita");
      }
      console.error("Errore copia IBAN", error);
    }
  };

  const renderAnswerText = (text) => {
    const boldRegex = /\*\*(.*?)\*\*/g;

    const renderBoldSegments = (line, lineIndex) => {
      const segments = line.split(boldRegex);
      return segments.map((segment, segmentIndex) =>
        segmentIndex % 2 === 1 ? (
          <strong key={`bold-${lineIndex}-${segmentIndex}`} className="font-semibold text-[#5c4329]">
            {segment}
          </strong>
        ) : (
          <span key={`text-${lineIndex}-${segmentIndex}`}>{segment}</span>
        ),
      );
    };

    return text.split("\n").map((line, lineIndex) => {
      if (line.trim() === "") {
        return <div key={`space-${lineIndex}`} className="h-3" aria-hidden="true" />;
      }

      return (
        <p key={`line-${lineIndex}`} className="text-[1.02rem] leading-relaxed sm:text-[1.08rem]">
          {renderBoldSegments(line, lineIndex)}
        </p>
      );
    });
  };

  const renderNewsText = (text) => {
    const boldRegex = /\*\*(.*?)\*\*/g;

    const renderAddressToken = (segment, isBold, lineIndex, segmentIndex) => {
      const tokenParts = segment.split(VENAFRO_ADDRESS_TOKEN);

      if (tokenParts.length === 1) {
        return isBold ? (
          <strong key={`news-bold-${lineIndex}-${segmentIndex}`} className="font-semibold text-[#4b2f12]">
            {segment}
          </strong>
        ) : (
          <span key={`news-text-${lineIndex}-${segmentIndex}`}>{segment}</span>
        );
      }

      const tokenChildren = [];
      tokenParts.forEach((part, partIndex) => {
        if (part) {
          tokenChildren.push(
            <span key={`news-token-text-${lineIndex}-${segmentIndex}-${partIndex}`}>{part}</span>,
          );
        }

        if (partIndex < tokenParts.length - 1) {
          tokenChildren.push(
            <a
              key={`news-token-link-${lineIndex}-${segmentIndex}-${partIndex}`}
              href={VENAFRO_ADDRESS_MAPS_URL}
              target="_blank"
              rel="noreferrer"
              className="news-inline-address-link"
              data-testid="news-venafro-address-link"
            >
              {VENAFRO_ADDRESS_LABEL}
            </a>,
          );
        }
      });

      return isBold ? (
        <strong key={`news-bold-${lineIndex}-${segmentIndex}`} className="font-semibold text-[#4b2f12]">
          {tokenChildren}
        </strong>
      ) : (
        <span key={`news-text-${lineIndex}-${segmentIndex}`}>{tokenChildren}</span>
      );
    };

    const renderBoldSegments = (line, lineIndex) => {
      const segments = line.split(boldRegex);
      return segments.map((segment, segmentIndex) =>
        renderAddressToken(segment, segmentIndex % 2 === 1, lineIndex, segmentIndex),
      );
    };

    return text.split("\n").map((line, lineIndex) => {
      if (line.trim() === "") {
        return <div key={`news-space-${lineIndex}`} className="h-3" aria-hidden="true" />;
      }

      return (
        <p key={`news-line-${lineIndex}`} className="news-message-line">
          {renderBoldSegments(line, lineIndex)}
        </p>
      );
    });
  };

  return (
    <div className="wedding-app" data-testid="wedding-invitation-page">
      <Toaster position="top-center" />

      <main className="invite-container" data-testid="wedding-page-content">
        <section className="reveal-block" data-reveal="true" data-testid="hero-section">
          <div className="hero-envelope section-panel" data-testid="hero-envelope-card">
            <div
              className="envelope-flap"
              style={{ transform: `perspective(900px) rotateX(${-(envelopeOpen * 172)}deg)` }}
              data-testid="hero-envelope-flap"
            />
            <div className="envelope-fold-left" data-testid="hero-envelope-fold-left" />
            <div className="envelope-fold-right" data-testid="hero-envelope-fold-right" />

            <div className="hero-content relative z-[3] px-6 pb-10 text-center sm:px-10">
              <div className="wax-seal" data-testid="hero-monogram">
                <span className="wax-seal-text" data-testid="hero-monogram-text">P &amp; A</span>
              </div>
              <p className="script-font mt-7 text-3xl text-[#7d6241]" data-testid="hero-main-title">
                Pierpaolo &amp; Angela
              </p>
              <p
                className="hero-date-reflection mt-2 text-lg tracking-[0.18em] text-[#876947]"
                data-testid="hero-date"
              >
                <span className="hero-date-main" data-text="26 settembre 2026">26 settembre 2026</span>
              </p>
              <div className="ornamental-divider my-5" data-testid="hero-divider" />

              <p className="text-base leading-relaxed text-[#5a4934]" data-testid="hero-intro-text">
                Abbiamo scelto di fermare il tempo, per dargli un nuovo significato.
              </p>
              <p
                className="mt-3 text-[0.98rem] leading-relaxed text-[#6b5841]"
                data-testid="hero-subtitle-text"
              >
                Con immensa gioia vi invitiamo a celebrare il giorno del nostro matrimonio.
                <br />
                Un momento unico, da vivere insieme alle persone più care. Un giorno speciale, da
                condividere con le persone che amiamo.
              </p>

              <div className="mt-6 flex items-center justify-center gap-2 text-[#8f7351]">
                <ChevronDown size={18} aria-hidden="true" />
                <p className="text-sm tracking-[0.14em]" data-testid="hero-scroll-tip">
                  Scorri verso il basso per leggere l&apos;invito
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-panel countdown-panel reveal-block mt-8" data-reveal="true" data-testid="countdown-section">
          <div className="px-6 py-8">
            <p className="section-eyebrow" data-testid="countdown-title">Il tempo dell&apos;attesa</p>
            <p className="section-intro" data-testid="countdown-intro">
              {countdown.expired ? (
                <strong>Pierpaolo &amp; Angela Sposi</strong>
              ) : (
                <strong>Ancora pochi istanti al nostro sì.</strong>
              )}
            </p>

            <div className="countdown-grid mt-6" data-testid="countdown-grid">
              {countdownLabels.map((item) => (
                <div className="countdown-chip" key={item.key} data-testid={`countdown-${item.key}-card`}>
                  <p className="countdown-value" data-testid={`countdown-${item.key}-value`}>
                    {String(countdown[item.key]).padStart(2, "0")}
                  </p>
                  <p className="countdown-label" data-testid={`countdown-${item.key}-label`}>{item.label}</p>
                </div>
              ))}
            </div>

          </div>
        </section>

        <section
          className="section-panel event-news-panel travel-panel reveal-block mt-8"
          data-reveal="true"
          data-testid="booklet-section"
        >
          <div className="px-6 py-7 space-y-3">
            <Button asChild className="gold-button h-12 w-full" data-testid="countdown-booklet-button">
              <a
                href="https://drive.google.com/file/d/1JUtfc1e3FuNGjw3PMPpHgzP0uNkAZmhn/view?usp=drivesdk"
                target="_blank"
                rel="noreferrer"
                data-testid="countdown-booklet-link"
              >
                📖 Libretto Cerimonia
              </a>
            </Button>

            <Button asChild className="gold-button h-12 w-full" data-testid="booklet-photo-button">
              <a
                href="https://my.guestpix.com/guest/193980/access/itfA7ggj"
                target="_blank"
                rel="noreferrer"
                data-testid="booklet-photo-link"
              >
                📷 Carica Foto
              </a>
            </Button>

            <Button asChild className="gold-button h-12 w-full" data-testid="booklet-route-button">
              <a
                href="https://maps.app.goo.gl/1JoZYTfjCANXwR2e7?g_st=ac"
                target="_blank"
                rel="noreferrer"
                data-testid="booklet-route-link"
              >
                🚗 Percorso Consigliato
              </a>
            </Button>
          </div>
        </section>

        <section
          className="section-panel event-news-panel travel-panel reveal-block mt-8"
          data-reveal="true"
          data-testid="travel-with-us-section"
        >
          <div className="px-6 py-8">
            <Accordion type="single" collapsible className="travel-main-accordion" data-testid="travel-main-accordion">
              <AccordionItem value="travel-main" className="travel-main-item" data-testid="travel-main-item">
                <AccordionTrigger className="travel-main-trigger" data-testid="travel-main-trigger">
                  <span className="travel-heading-row">
                    <Bike size={30} className="travel-heading-icon" aria-hidden="true" data-testid="travel-with-us-icon" />
                    <span className="section-title travel-heading" data-testid="travel-with-us-title">In viaggio con Noi</span>
                  </span>
                </AccordionTrigger>

                <AccordionContent className="travel-main-content" data-testid="travel-main-content">
                  <Accordion
                    type="single"
                    collapsible
                    className="travel-subsections"
                    data-testid="travel-subsections-accordion"
                  >
                    <AccordionItem value="travel-bus" className="travel-block" data-testid="travel-bus-block">
                      <AccordionTrigger className="travel-block-title" data-testid="travel-bus-title">
                        <strong>🚌 Per chi partirà da Ceppagna in autobus</strong>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="travel-block-text" data-testid="travel-bus-text-1">
                          Dopo il tradizionale rinfresco a casa della sposa, l’autobus partirà da <strong>Piazza Padre Pio</strong> alle <strong>ore 13:00</strong>.
                        </p>
                        <p className="travel-block-text" data-testid="travel-bus-text-2">
                          <strong>Per tutte le informazioni relative alla partenza, all’organizzazione e alla gestione del gruppo, il punto di riferimento sarà il nostro coordinatore ufficiale NICANDRO MATTEO 👮</strong>
                        </p>
                        <p className="travel-block-text" data-testid="travel-bus-text-3">
                          Vi raccomandiamo quindi la <strong>massima puntualità: <u>alle 13:00 l’autobus parte!</u></strong>
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="travel-car" className="travel-block" data-testid="travel-car-block">
                      <AccordionTrigger className="travel-block-title" data-testid="travel-car-title">
                        <strong>🚗 Per chi viaggia in auto: itinerario consigliato per il ricevimento</strong>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="travel-block-text" data-testid="travel-car-text">
                          Per raggiungere il <strong>ristorante</strong> dalla <strong>Basilica di Capodimonte</strong>, vi consigliamo di seguire l’itinerario che potrà essere visualizzato su <strong>Google Maps</strong> cliccando il pulsante qui sotto.
                        </p>

                        <Button
                          asChild
                          className="gold-button mt-4 h-12 w-full"
                          data-testid="travel-route-button"
                        >
                          <a href={TRAVEL_ROUTE_MAP_URL} target="_blank" rel="noreferrer" data-testid="travel-route-link">
                            VISUALIZZA ITINERARIO
                          </a>
                        </Button>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="travel-parking" className="travel-block" data-testid="travel-parking-block">
                      <AccordionTrigger className="travel-block-title" data-testid="travel-parking-title">
                        <strong>🅿️ Parcheggi</strong>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="travel-block-text" data-testid="travel-parking-text-1">
                          Per chi arriverà in auto, niente pensieri anche per il parcheggio: sarà disponibile <strong>gratuitamente e in area custodita</strong> sia presso la <strong>Basilica</strong> sia presso la <strong>location del ricevimento</strong>.
                        </p>
                        <p className="travel-block-text" data-testid="travel-parking-text-2">
                          Un pensiero in meno per godervi la giornata insieme a noi, dall’inizio alla fine.
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <section className="section-panel event-news-panel timeline-panel reveal-block mt-8" data-reveal="true" data-testid="timeline-section">
          <div className="px-6 py-8">
            <h2 className="section-title timeline-heading" data-testid="timeline-title">
              <span className="timeline-heading-row">
                <Heart size={28} className="timeline-title-icon" aria-hidden="true" data-testid="timeline-title-icon" />
                <span>La nostra giornata</span>
              </span>
            </h2>
            <p className="section-intro leading-relaxed" data-testid="timeline-intro">
              Abbiamo immaginato una giornata da vivere insieme passo dopo passo:
              <br />
              dall’emozione della cerimonia alla leggerezza dei festeggiamenti,
              <br />
              fino ai brindisi, alla musica e ai momenti da condividere.
            </p>

            <div className="timeline-wrapper mt-7" data-testid="timeline-list">
              {timelineItems.map((step, index) => (
                <div className="timeline-item" key={step} data-testid={`timeline-item-${index + 1}`}>
                  <div className="timeline-dot" aria-hidden="true" />
                  <div className="timeline-card">
                    <Clock3 size={15} aria-hidden="true" />
                    <p data-testid={`timeline-item-${index + 1}-text`}>{step}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-panel event-news-panel reveal-block mt-8" data-reveal="true" data-testid="event-news-section">
          <div className="px-6 py-8">
            <h2 className="section-title event-news-heading" data-testid="event-news-title">
              <span className="event-news-heading-row">
                <BellRing size={34} className="event-news-title-icon" aria-hidden="true" data-testid="event-news-title-icon" />
                <span>News sul Matrimonio</span>
              </span>
            </h2>
            <p className="section-intro" data-testid="event-news-intro">
              Tutte le informazioni utili, sempre aggiornate.
            </p>

            <Accordion
              type="single"
              collapsible
              className="mt-5"
              data-testid="event-news-accordion"
            >
              {eventNewsItems.map((news, index) => {
                const isLatestNews = index === 0;

                return (
                <AccordionItem
                  value={`news-${index}`}
                  key={news.title}
                  className="event-news-item"
                  data-testid={`event-news-item-${index + 1}`}
                >
                  <AccordionTrigger
                    className="text-[1rem] text-[#5b4935]"
                    data-testid={`event-news-trigger-${index + 1}`}
                  >
                    <div className="news-trigger-content" data-testid={`event-news-trigger-content-${index + 1}`}>
                      <div className="news-title-row">
                        {isLatestNews && (
                          <span
                            className="news-recent-dot"
                            data-testid="event-news-latest-dot"
                            aria-hidden="true"
                          />
                        )}
                        <span
                          className={isLatestNews ? "news-latest-title" : ""}
                          data-testid={isLatestNews ? "event-news-latest-title" : undefined}
                        >
                          {news.title}
                        </span>
                      </div>
                      <span className="news-date" data-testid={`event-news-date-${index + 1}`}>
                        Pubblicato il {news.publishedOn}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-[1rem] leading-relaxed text-[#4f3b25]"
                    data-testid={`event-news-message-${index + 1}`}
                  >
                    <div className="news-message-body" data-testid={`event-news-message-body-${index + 1}`}>
                      {news.message && (
                        <div className="news-message-text" data-testid={`event-news-message-text-${index + 1}`}>
                          {renderNewsText(news.message)}
                        </div>
                      )}

                      {news.quote && (
                        <p className="news-message-quote" data-testid={`event-news-message-quote-${index + 1}`}>
                          {news.quote}
                        </p>
                      )}

                      {news.closing && (
                        <div className="news-message-text news-message-closing" data-testid={`event-news-message-closing-${index + 1}`}>
                          {renderNewsText(news.closing)}
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </section>

        <section className="reveal-block mt-8" data-reveal="true" data-testid="location-section">
          <div className="px-2">
            <h2 className="section-title" data-testid="location-title">Location</h2>
            <p className="section-intro" data-testid="location-intro">
              I luoghi del nostro giorno speciale.
            </p>
          </div>

          <div className="mt-5 grid gap-4">
            {locationBlocks.map((location) => (
              <Card key={location.key} className="section-panel border-[#dcc8aa]/70 shadow-sm" data-testid={`location-${location.key}-card`}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-[1.35rem] text-[#6d5337]" data-testid={`location-${location.key}-name`}>
                    <MapPin size={18} aria-hidden="true" /> {location.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-lg text-[#4c3b2a]" data-testid={`location-${location.key}-venue`}>{location.name}</p>
                  <p className="mt-1 text-sm text-[#6f5c46]" data-testid={`location-${location.key}-address`}>{location.address}</p>
                  <p className="mt-2 text-sm text-[#6f5c46]" data-testid={`location-${location.key}-phone`}>
                    Telefono: {location.phone}
                  </p>
                  <p className="mt-1 text-sm text-[#6f5c46]" data-testid={`location-${location.key}-website`}>
                    Sito web:{" "}
                    <a
                      href={location.website}
                      target="_blank"
                      rel="noreferrer"
                      className="underline decoration-[#b88d55] underline-offset-2"
                      data-testid={`location-${location.key}-website-link`}
                    >
                      {location.website}
                    </a>
                  </p>
                  <div className="location-map-shell mt-4" data-testid={`location-${location.key}-map-embed-wrapper`}>
                    <iframe
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(location.mapQuery)}&z=15&output=embed`}
                      title={`Mappa ${location.title}`}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="location-map-frame"
                      data-testid={`location-${location.key}-map-embed`}
                    />
                  </div>
                  <Button asChild className="gold-button mt-5 h-11 w-full" data-testid={`location-${location.key}-map-button`}>
                    <a
                      href={location.mapUrl}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`location-${location.key}-map-link`}
                    >
                      Apri mappa
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="section-panel reveal-block mt-8" data-reveal="true" data-testid="faq-section">
          <div className="px-6 py-8">
            <h2 className="section-title" data-testid="faq-title">FAQ</h2>
            <p className="section-intro" data-testid="faq-intro">Alcune informazioni utili.</p>

            <Accordion
              type="single"
              collapsible
              className="mt-4 space-y-4"
              data-testid="faq-sections-wrapper"
            >
              {faqSections.map((section, sectionIndex) => (
                <AccordionItem
                  value={`section-${section.key}`}
                  key={section.key}
                  className="rounded-xl border border-[#dbc6a7]/60 bg-[#fffaf3]/55 px-4"
                  data-testid={`faq-category-${section.key}`}
                >
                  <AccordionTrigger
                    className="py-4 text-[1.35rem] text-[#5c452c] hover:no-underline"
                    style={{ fontFamily: '"Italiana", serif' }}
                    data-testid={`faq-category-title-${sectionIndex + 1}`}
                  >
                    {section.title}
                  </AccordionTrigger>

                  <AccordionContent data-testid={`faq-section-content-${section.key}`}>
                    <Accordion
                      type="single"
                      collapsible
                      className="pb-2"
                      data-testid={`faq-accordion-${section.key}`}
                    >
                      {section.items.map((faq, faqIndex) => (
                        <AccordionItem
                          value={`${section.key}-${faqIndex}`}
                          key={`${section.key}-${faq.question}`}
                          data-testid={`faq-${section.key}-item-${faqIndex + 1}`}
                        >
                          <AccordionTrigger
                            className="text-[1rem] text-[#5b4935]"
                            data-testid={`faq-${section.key}-question-${faqIndex + 1}`}
                          >
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent
                            className="text-[1rem] leading-relaxed text-[#6f5c45] sm:text-[1.05rem]"
                            data-testid={`faq-${section.key}-answer-${faqIndex + 1}`}
                          >
                            <div className="space-y-1">{renderAnswerText(faq.answer)}</div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="section-panel reveal-block mt-8" data-reveal="true" data-testid="gift-list-section">
          <div className="px-6 py-8 text-center">
            <h2 className="section-title" data-testid="gift-list-title">Lista nozze</h2>
            <p className="gift-list-message mt-5" data-testid="gift-list-description">
              Con la vostra presenza in questo giorno
              <br />
              avremo già tutto ciò che conta davvero.
              <br />
              <br />
              Ma ciò che stiamo costruendo va oltre un solo momento:
              <br />
              è un tempo, uno spazio, una vita insieme.
            </p>

            <Button onClick={handleIbanToggle} className="gold-button mt-6 h-12 w-full" data-testid="gift-list-iban-button">
              <Gift size={16} aria-hidden="true" /> Scopri i dettagli
            </Button>

            {showIban && (
              <div className="iban-card mt-4" data-testid="gift-list-iban-value">
                <p className="iban-label" data-testid="gift-list-iban-label">IBAN</p>
                <div className="iban-row" data-testid="gift-list-iban-row">
                  <p className="iban-value" data-testid="gift-list-iban-text">{ibanDetails.iban}</p>
                  <button
                    type="button"
                    onClick={handleCopyIban}
                    className="iban-copy-button"
                    data-testid="gift-list-iban-copy-button"
                  >
                    Copia
                  </button>
                </div>
                <div className="iban-details" data-testid="gift-list-bank-details">
                  <p data-testid="gift-list-bank-name">Banca: {ibanDetails.bank}</p>
                  <p data-testid="gift-list-bank-bic">BIC/SWIFT: {ibanDetails.bic}</p>
                  <p data-testid="gift-list-bank-holder">Intestatari: {ibanDetails.holders}</p>
                </div>
                <p className="iban-closure" data-testid="gift-list-closing-text">
                  Ogni gesto troverà il suo posto nel nostro futuro.
                </p>
              </div>
            )}
          </div>
        </section>

        <section
          className="section-panel reveal-block mt-8"
          data-reveal="true"
          data-testid="contacts-section"
        >
          <div className="px-6 py-8">
            <h2 className="section-title" data-testid="contacts-title">Contatti degli Sposi</h2>
            <p className="section-intro" data-testid="contacts-intro">
              Se doveste avere dubbi, necessitare di informazioni o semplicemente preferire un
              contatto diretto, saremo felici di aiutarvi.
              <br />
              <br />
              Di seguito trovate i nostri recapiti.
            </p>

            <Accordion
              type="single"
              collapsible
              className="mt-5"
              data-testid="contacts-accordion"
            >
              {spouseContacts.map((contact, index) => (
                <AccordionItem
                  value={contact.key}
                  key={contact.key}
                  data-testid={`contacts-item-${contact.key}`}
                >
                  <AccordionTrigger
                    className="text-[1.05rem] text-[#5b4935]"
                    data-testid={`contacts-trigger-${contact.key}`}
                  >
                    {contact.title}
                  </AccordionTrigger>

                  <AccordionContent
                    className="text-sm leading-relaxed text-[#6f5c45]"
                    data-testid={`contacts-content-${contact.key}`}
                  >
                    <div className="space-y-2 rounded-xl border border-[#dcc8aa]/60 bg-[#fffaf3]/55 p-4" data-testid={`contacts-card-${contact.key}`}>
                      <p className="text-base font-semibold text-[#5b4630]" data-testid={`contacts-name-${contact.key}`}>
                        {contact.name}
                      </p>
                      <p data-testid={`contacts-address-${contact.key}`}>Indirizzo: {contact.address}</p>
                      <p data-testid={`contacts-phone-${contact.key}`}>
                        Telefono:{" "}
                        <a
                          href={`tel:${contact.telLink}`}
                          className="underline decoration-[#b88d55] underline-offset-2"
                          data-testid={`contacts-phone-link-${contact.key}`}
                        >
                          {contact.phone}
                        </a>
                      </p>
                      <p data-testid={`contacts-email-${contact.key}`}>
                        Mail:{" "}
                        <a
                          href={`mailto:${contact.email}`}
                          className="underline decoration-[#b88d55] underline-offset-2"
                          data-testid={`contacts-email-link-${contact.key}`}
                        >
                          {contact.email}
                        </a>
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <footer className="reveal-block pb-3 pt-10 text-center" data-reveal="true" data-testid="footer-section">
          <p className="text-sm text-[#6b5844]" data-testid="footer-message">
            Non vediamo l’ora di festeggiare con voi
          </p>
          <div className="mx-auto mt-4 w-fit rounded-full border border-[#dcc5a2] px-4 py-1 text-sm tracking-[0.32em] text-[#876947]" data-testid="footer-monogram">
            P &amp; A
          </div>
        </footer>
      </main>
    </div>
  );
}

export default App;
